import { Dropdown, Icons, Input } from '@commenda/kakashi';
import { TEquityInfo } from '@commenda/types/src/incorporation/types';
import { TCurrencyInfo } from '@commenda/types/src/random';
import { CurrencyHelper } from '@commenda/utils';
import { Currency } from '@prisma/client';
import React, { useEffect, useState } from 'react';

type CompanyValuationInputProps = {
  equityInfo: Partial<TEquityInfo>;
  setEquityInfo: React.Dispatch<React.SetStateAction<TEquityInfo>>;
  shareCountPlaceholder: string;
  sharePricePlaceholder: string;
  currency?: TCurrencyInfo;
  helpTexts?: {
    mainHeading?: string;
    bodyText?: string;
    connectingText?: string;
  };
  isNumberOfSharesApplicable?: boolean;
  setSelectedCurrency?: React.Dispatch<React.SetStateAction<TCurrencyInfo>>;
};

const CompanyValuationInput = ({
  equityInfo,
  setEquityInfo,
  shareCountPlaceholder,
  sharePricePlaceholder,
  currency = CurrencyHelper.DEFAULT_CURRENCY_INFO,
  helpTexts,
  isNumberOfSharesApplicable = true,
  setSelectedCurrency,
}: CompanyValuationInputProps) => {
  const [convertedAmount, setConvertedAmount] = useState<string>('');

  function getCurrencyItem(selectedCurrency: Currency) {
    const currencyInfo = CurrencyHelper.getCurrencyInfo(selectedCurrency);
    return {
      label: `${currencyInfo.name} ${currencyInfo.style}`,
      onClick: () => setSelectedCurrency(currencyInfo),
      value: currencyInfo.name,
    };
  }

  const dropdownMenu = {
    groups: [
      {
        label: 'North America',
        items: [Currency.USD, Currency.CAD, Currency.MXN].map(getCurrencyItem),
      },
      {
        label: 'South America',
        items: [
          Currency.BRL,
          Currency.ARS,
          Currency.CLP,
          Currency.COP,
          Currency.PEN,
          Currency.UYU,
        ].map(getCurrencyItem),
      },
      {
        label: 'Europe',
        items: [
          Currency.EUR,
          Currency.GBP,
          Currency.CHF,
          Currency.PLN,
          Currency.CZK,
          Currency.HUF,
          Currency.DKK,
          Currency.NOK,
          Currency.SEK,
          Currency.BGN,
          Currency.RON,
          Currency.HRK,
        ].map(getCurrencyItem),
      },
      {
        label: 'East Asia',
        items: [
          Currency.CNY,
          Currency.JPY,
          Currency.KRW,
          Currency.TWD,
          Currency.MNT,
        ].map(getCurrencyItem),
      },
      {
        label: 'Southeast Asia',
        items: [
          Currency.SGD,
          Currency.THB,
          Currency.VND,
          Currency.IDR,
          Currency.MYR,
          Currency.MMK,
          Currency.KHR,
          Currency.LAK,
          Currency.PHP,
        ].map(getCurrencyItem),
      },
      {
        label: 'South Asia',
        items: [
          Currency.INR,
          Currency.BDT,
          Currency.LKR,
          Currency.NPR,
          Currency.PKR,
        ].map(getCurrencyItem),
      },
      {
        label: 'Middle East',
        items: [
          Currency.AED,
          Currency.SAR,
          Currency.ILS,
          Currency.QAR,
          Currency.BHD,
          Currency.KWD,
          Currency.OMR,
          Currency.IRR,
        ].map(getCurrencyItem),
      },
      {
        label: 'Oceania',
        items: [Currency.AUD, Currency.NZD, Currency.FJD].map(getCurrencyItem),
      },
      {
        label: 'Africa',
        items: [
          Currency.ZAR,
          Currency.EGP,
          Currency.NGN,
          Currency.MAD,
          Currency.KES,
          Currency.TND,
          Currency.GHS,
        ].map(getCurrencyItem),
      },
    ],
  };

  useEffect(() => {
    const updateConvertedAmount = async () => {
      if (equityInfo.price) {
        try {
          const converted = await CurrencyHelper.convertCurrency(
            currency.currency,
            Currency.SGD,
            equityInfo.price
          );
          setConvertedAmount(
            CurrencyHelper.formatCurrency(Currency.SGD, converted)
          );
        } catch (error) {
          console.error('Conversion failed:', error);
          setConvertedAmount(`${equityInfo.price.toFixed(2)} SGD`);
        }
      }
    };

    updateConvertedAmount();
  }, [equityInfo.price, currency.name]);

  return (
    <div className="flex flex-col gap-10 border-b pb-10">
      <div>
        <h3 className="text-base">
          {helpTexts?.mainHeading || 'Authorized Shares'}
        </h3>
        <span className="text-sm text-neutral-2">
          {helpTexts?.bodyText ||
            'We suggest you to keep the following default values to minimize corporate taxes.'}
        </span>
      </div>
      <div className="flex flex-col gap-7">
        {isNumberOfSharesApplicable ? (
          <div className="flex items-center gap-6">
            <div className="flex flex-col gap-1 shrink-0 grow ">
              <Input
                type="text"
                maxLength={15}
                placeholder={shareCountPlaceholder}
                value={equityInfo?.shareCount?.toLocaleString()}
                onChange={(e) => {
                  const regexThatMatchesNumberWithCommas = /^[0-9][0-9,]*$/;

                  const newValue = e.currentTarget.value;

                  if (!newValue) {
                    setEquityInfo((current) => ({
                      ...current,
                      shareCount: 0,
                    }));
                    return;
                  }

                  if (regexThatMatchesNumberWithCommas.test(newValue)) {
                    const parsedValue = parseInt(
                      newValue.replaceAll(',', ''),
                      10
                    );
                    setEquityInfo((current) => ({
                      ...current,
                      shareCount: parsedValue,
                    }));
                  }
                }}
              />
            </div>
            <p className="body-15 shrink-0">
              {helpTexts?.connectingText || 'issued shares valued at'}
            </p>
            <div className="relative shrink-0 grow">
              <div className="flex flex-col gap-1">
                <Input
                  className="text-right hide-arrow pl-6"
                  type="number"
                  name="price"
                  id="price"
                  min={0}
                  maxLength={9}
                  placeholder={sharePricePlaceholder}
                  value={equityInfo.price}
                  onChange={(e) => {
                    const currentValue = e.target.value;
                    if (currentValue.includes('.')) {
                      const [intPart, decimalPart] = currentValue.split('.');
                      if (decimalPart.length > 2) return;
                      const formattedIntPart =
                        intPart.replace(/^0+/, '') || '0';
                      e.target.value = `${formattedIntPart}.${decimalPart}`;
                    } else {
                      // For non-decimal numbers, remove leading zeros but allow a single zero
                      e.target.value = currentValue.replace(/^0+/, '') || '0';
                    }
                    const newValue = parseFloat(e.target.value);

                    if (
                      !Number.isNaN(newValue) &&
                      newValue >= 0 &&
                      newValue <= 100000000
                    ) {
                      setEquityInfo((current) => ({
                        ...current,
                        price: newValue,
                      }));
                    }
                  }}
                />
              </div>
              <span className="absolute top-2.5 left-3 select-none touch-none text-neutral-4">
                <Dropdown
                  menu={dropdownMenu}
                  contentClassName="max-h-40 overflow-y-auto"
                >
                  <span className="text-neutral-2 flex items-center gap-2">
                    {currency.name}
                    <Icons.ChevronDown height={22.5} width={22.5} />
                  </span>
                </Dropdown>
              </span>
              {currency.name !== 'SGD' ? (
                <p className="text-neutral-2 absolute left-1 mt-1 top-full text-[14px]">
                  This amounts to{' '}
                  <span className="font-bold text-neutral-600">
                    {`${convertedAmount} SGD`}
                  </span>
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CompanyValuationInput;
